import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Leaf, Zap, Car, ChevronRight, Clock, Shield, Wifi, 
  Plane, MapPin, ArrowRight, Calculator, Map, Gift, 
  Percent, Users, Briefcase, Star, CheckCircle, Moon, Sun,
  Droplets, Battery, Languages, Building, Calendar, FileText,
  UserPlus, Share2, Award, MessageSquare, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { PriceSimulator } from '../components/PriceSimulator';
import { QuoteRequestModal } from '../components/QuoteRequestModal';
import { LanguageSelector } from '../components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Airport transfer prices
const AIRPORT_TRANSFERS = [
  { from: "Aéroport Nice", to: "Nice Ville", oneWay: 25, roundTrip: 45, time: "20 min", seoPath: "/vtc-aeroport-nice-centre" },
  { from: "Aéroport Nice", to: "Cannes", oneWay: 60, roundTrip: 110, time: "35 min", seoPath: "/transfert-aeroport-nice-cannes" },
  { from: "Aéroport Nice", to: "Monaco", oneWay: 65, roundTrip: 120, time: "30 min", seoPath: "/transfert-aeroport-nice-monaco" },
  { from: "Aéroport Nice", to: "Saint-Tropez", oneWay: 180, roundTrip: 300, time: "1h30", hasCondition: true, seoPath: "/transfert-aeroport-nice-saint-tropez" },
];

// Tesla images
const TESLA_IMAGES = {
  chauffeur: "/images/chauffeur-tesla.png",
  interior: "https://images.unsplash.com/photo-1758411898607-9ac9a6c4dd0c?crop=entropy&cs=srgb&fm=jpg&w=800",
  airport: "/images/chauffeur-aeroport.png",
};

// SEO Keywords
const SEO_CITIES = ['Nice', 'Cannes', 'Monaco', 'Saint-Tropez', 'Antibes', 'Menton', 'Grasse', 'Fréjus'];


export const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showSimulator, setShowSimulator] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [activeImage, setActiveImage] = useState('chauffeur');
  const [dynamicReviews, setDynamicReviews] = useState([]);

  // Auto-rotate images every 4 seconds
  const imageKeys = ['chauffeur', 'interior', 'airport'];
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage(current => {
        const currentIndex = imageKeys.indexOf(current);
        const nextIndex = (currentIndex + 1) % imageKeys.length;
        return imageKeys[nextIndex];
      });
    }, 4000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/evaluations/recent`);
        if (res.data?.length > 0) setDynamicReviews(res.data);
      } catch (e) { /* fallback reviews */ }
    };
    fetchReviews();
  }, []);

  const handleBooking = () => {
    navigate('/quick-booking');
  };

  const handleAirportBooking = (transfer) => {
    navigate(transfer.seoPath);
  };

  return (
    <div className="min-h-screen bg-[#09090b] relative overflow-hidden">
      {/* SEO Hidden H1 */}
      <h1 className="sr-only">VTC Nice - Chauffeur Privé Nice Monaco Cannes Saint-Tropez - YOUMI GREEN VTC - Navette Aéroport Nice Côte d'Azur</h1>
      
      {/* Hero gradient */}
      <div className="absolute inset-0 hero-gradient pointer-events-none" />
      
      {/* Navigation */}
      <nav className="relative z-10 px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-2 sm:gap-3">
          <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-10 h-10 sm:w-12 sm:h-12" />
          <div className="flex flex-col">
            <span className="font-bold text-lg sm:text-xl font-['Outfit'] text-white leading-tight">YOUMI GREEN</span>
            <span className="text-emerald-500 font-bold text-xs sm:text-sm font-['Outfit'] tracking-widest">VTC</span>
          </div>
        </div>
        
        <div className="flex items-center gap-1 sm:gap-3">
          <LanguageSelector />
          {user ? (
            <>
              <Button 
                variant="ghost" 
                className="text-zinc-400 hover:text-white text-[11px] sm:text-sm px-1 sm:px-3"
                onClick={() => navigate('/client')}
                data-testid="client-center-btn"
              >
                Mon profil
              </Button>
              <Button 
                className="btn-primary text-[11px] sm:text-base px-2 sm:px-4"
                onClick={() => navigate('/quick-booking')}
                data-testid="nav-book-btn"
              >
                {t('nav_book')}
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="ghost" 
                className="text-zinc-400 hover:text-white text-[11px] sm:text-sm px-1 sm:px-3"
                onClick={() => navigate('/auth')}
                data-testid="login-link"
              >
                {t('nav_login')}
              </Button>
              <Button 
                className="btn-primary text-[11px] sm:text-base px-2 sm:px-4"
                onClick={() => navigate('/auth')}
                data-testid="signup-btn"
              >
                {t('nav_signup')}
              </Button>
            </>
          )}
        </div>
      </nav>

      {/* Hero Section - SEO optimized */}
      <section className="relative z-10 px-6 pt-12 pb-20 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="flex gap-2 mb-6">
              <span className="eco-badge">
                <Leaf className="w-3 h-3" />
                {t('hero_badge')}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-widest bg-amber-500/10 border border-amber-500/30 text-amber-400">
                <Gift className="w-3 h-3" />
                -10% {t('promo_online_payment')}
              </span>
            </div>
            
            {/* SEO H2 */}
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] leading-tight mb-6">
              {t('hero_title_1')}{' '}
              <span className="text-emerald-500">{t('hero_title_2')}</span>,{' '}
              <span className="text-emerald-500">{t('hero_title_3')}</span>{' '}
              {t('hero_title_4')}{' '}
              <span className="text-emerald-500">{t('hero_title_5')}</span>
            </h2>
            
            <p className="text-lg text-zinc-400 mb-8 max-w-xl">
              {t('hero_subtitle')}
            </p>
            
            <div className="flex flex-wrap gap-4 mb-4">
              <Button 
                className="btn-primary text-lg px-8 py-4 flex items-center gap-2"
                onClick={handleBooking}
                data-testid="hero-book-btn"
              >
                {t('hero_cta')}
                <ChevronRight className="w-5 h-5" />
              </Button>
              <Button 
                className="btn-secondary text-lg px-8 py-4 flex items-center gap-2"
                onClick={() => setShowSimulator(true)}
                data-testid="simulator-btn"
              >
                <Calculator className="w-5 h-5" />
                {t('hero_estimate')}
              </Button>
            </div>
            <p className="text-emerald-400 font-semibold text-sm mb-8 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {t('fixed_price_badge')}
            </p>

            {/* Promo code */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl mb-8">
              <p className="text-amber-400 font-medium flex items-center gap-2">
                <Percent className="w-5 h-5" />
                {t('promo_code_label')} : <span className="font-bold text-lg">FIRST10</span> = {t('promo_first_booking')}
              </p>
            </div>
          </div>
          
          {/* Tesla Gallery */}
          <div className="relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="absolute -inset-4 bg-emerald-500/20 blur-3xl rounded-full" />
            <div className="relative glass-card rounded-3xl overflow-hidden">
              <img 
                src={TESLA_IMAGES[activeImage]}
                alt="VTC Nice Tesla Model Y - Chauffeur privé Côte d'Azur"
                className="w-full h-80 object-cover transition-all duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                <p className="text-white font-bold text-xl font-['Outfit']">Tesla Model Y</p>
                <p className="text-zinc-400">Blanche • Intérieur Rouge • 100% Électrique</p>
                
                <div className="flex gap-2 mt-4">
                  {Object.keys(TESLA_IMAGES).map((key) => (
                    <button
                      key={key}
                      onClick={() => setActiveImage(key)}
                      className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                        activeImage === key ? 'border-emerald-500' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={TESLA_IMAGES[key]} 
                        alt={key} 
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Showcase - Vitrine */}
      <section className="relative z-10 px-6 py-16 max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full text-emerald-400 text-sm mb-4">
            <Car className="w-4 h-4" />
            {t('video_badge')}
          </span>
          <h2 className="text-3xl font-bold text-white font-['Outfit']">
            {t('video_title_1')} <span className="text-emerald-500">{t('video_title_2')}</span>
          </h2>
        </div>
        <div className="video-showcase max-w-4xl mx-auto" style={{ aspectRatio: '16/9' }}>
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="/logo-youmi.png"
            className="rounded-2xl"
            data-testid="promo-video"
          >
            <source src="/promo-youmi.mp4" type="video/mp4" />
          </video>
          <div className="video-showcase-overlay" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 z-10">
            <div className="flex items-center gap-3 mb-3">
              <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-12 h-12" />
              <div>
                <p className="text-white font-bold text-xl font-['Outfit']">YOUMI GREEN VTC</p>
                <p className="text-zinc-400 text-sm">{t('video_company')}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <Button className="btn-primary" onClick={handleBooking} data-testid="video-book-btn">
                {t('video_cta')} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button 
                className="btn-secondary"
                onClick={() => navigate('/demande-devis')}
                data-testid="video-quote-btn"
              >
                <FileText className="w-4 h-4 mr-1" />
                {t('video_quote')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Referral + Loyalty Program Section */}
      <section className="relative z-10 px-6 py-16 bg-gradient-to-r from-emerald-900/20 to-amber-900/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-full text-amber-400 text-sm mb-4">
              <Star className="w-4 h-4" />
              {t('loyalty_title')}
            </div>
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
              {t('loyalty_rewards').split(' ').slice(0, -1).join(' ')} <span className="text-emerald-500">{t('loyalty_rewards').split(' ').slice(-1)}</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {/* Referral */}
            <div className="glass-card rounded-2xl p-6 text-center card-hover border-2 border-purple-500/30">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">{t('loyalty_referral')}</h3>
              <p className="text-zinc-400 text-sm">{t('loyalty_referral_desc')}</p>
              <p className="text-purple-400 font-bold text-2xl mt-2">{t('loyalty_referral_amount')}</p>
            </div>

            <div className="glass-card rounded-2xl p-6 text-center card-hover">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Percent className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">{t('loyalty_online_discount')}</h3>
              <p className="text-zinc-400 text-sm">{t('loyalty_online_discount_desc')}</p>
              <p className="text-emerald-400 font-bold mt-3">Code: FIRST10</p>
            </div>
            
            <div className="glass-card rounded-2xl p-6 text-center card-hover">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">{t('loyalty_5_rides')}</h3>
              <p className="text-zinc-400 text-sm">{t('loyalty_5_rides_desc')}</p>
              <p className="text-emerald-400 font-bold mt-3">{t('loyalty_automatic')}</p>
            </div>
            
            <div className="glass-card rounded-2xl p-6 text-center card-hover">
              <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-pink-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-2">{t('loyalty_10th_free')}</h3>
              <p className="text-zinc-400 text-sm">{t('loyalty_10th_desc')}</p>
              <p className="text-pink-400 font-bold mt-3">{t('loyalty_gift')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Airport Transfers - SEO Section */}
      <section id="airport" className="relative z-10 px-6 py-20 bg-[#121212]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full text-emerald-400 text-sm mb-4">
              <Plane className="w-4 h-4" />
              {t('airport_subtitle')}
            </div>
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
              <span className="text-emerald-500">{t('airport_title')}</span> - {t('airport_fixed_rates')}
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Service de <strong>navette aéroport Nice</strong> vers Cannes, Monaco, Saint-Tropez et Nice centre. 
              Votre <strong>chauffeur privé Nice</strong> vous attend avec pancarte nominative.
            </p>
          </div>

          {/* Included services */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {[
              { icon: <Users className="w-4 h-4" />, text: t('airport_welcome_sign') },
              { icon: <Plane className="w-4 h-4" />, text: t('airport_flight_tracking') },
              { icon: <Clock className="w-4 h-4" />, text: t('airport_free_wait') },
              { icon: <Briefcase className="w-4 h-4" />, text: t('airport_luggage') },
              { icon: <CheckCircle className="w-4 h-4" />, text: t('airport_no_surprise') },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-full text-sm text-zinc-300">
                <span className="text-emerald-500">{item.icon}</span>
                {item.text}
              </div>
            ))}
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {AIRPORT_TRANSFERS.map((transfer, index) => (
              <div 
                key={index}
                className="glass-card rounded-2xl p-6 card-hover"
              >
                <div className="text-center mb-4">
                  <p className="text-zinc-400 text-sm">{transfer.from}</p>
                  <ArrowRight className="w-4 h-4 text-emerald-500 mx-auto my-2" />
                  <p className="text-white font-bold text-lg">{transfer.to}</p>
                  <p className="text-xs text-zinc-500 mt-1">~{transfer.time}</p>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center p-3 bg-zinc-900 rounded-lg">
                    <span className="text-zinc-400 text-sm">{t('airport_one_way')}</span>
                    <span className="text-2xl font-bold text-emerald-500 font-['Outfit']">
                      {transfer.oneWay}€<sup className="text-xs ml-0.5 text-amber-400">¹</sup>
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                    <span className="text-emerald-400 text-sm">{t('airport_round_trip')}</span>
                    <span className="text-2xl font-bold text-emerald-400 font-['Outfit']">
                      {transfer.roundTrip}€<sup className="text-xs ml-0.5 text-amber-400">¹</sup>{transfer.hasCondition && <sup className="text-sm ml-0.5">*</sup>}
                    </span>
                  </div>
                </div>
                
                {transfer.hasCondition && (
                  <p className="text-xs text-zinc-500 text-center mb-3 italic">
                    * {t('airport_same_day')}
                  </p>
                )}
                
                <Button 
                  className="w-full btn-primary text-sm py-2"
                  onClick={() => handleAirportBooking(transfer)}
                  data-testid={`airport-${index}`}
                >
                  {t('airport_book')}
                </Button>
              </div>
            ))}
          </div>

          {/* Privilege client note + Saint-Tropez condition */}
          <div className="mt-6 max-w-3xl mx-auto space-y-2">
            <p className="text-amber-400/90 text-xs text-center" data-testid="airport-privilege-footnote">
              <span className="text-amber-400 font-semibold">¹ {t('airport_privilege_note_label')}</span> {t('airport_privilege_note_text')}
            </p>
            <p className="text-zinc-500 text-xs text-center">
              <span className="text-emerald-400">*</span> {t('airport_st_tropez_note')}
            </p>
          </div>

          {/* Price guarantee */}
          <div className="mt-8 p-5 bg-emerald-500/10 rounded-xl border border-emerald-500/30 max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 text-emerald-400 font-semibold mb-2">
              <Shield className="w-5 h-5" />
              {t('price_guarantee_title')}
            </div>
            <p className="text-zinc-400 text-sm">{t('price_guarantee_full')}</p>
          </div>

          {/* Privileged client program — promotional banner */}
          <div
            className="mt-10 p-6 md:p-8 rounded-2xl border border-amber-400/40 max-w-4xl mx-auto"
            style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(16,185,129,0.10) 100%)' }}
            data-testid="privilege-program-banner"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-amber-400/20 border border-amber-400/40 flex items-center justify-center">
                <Award className="w-7 h-7 text-amber-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs font-bold uppercase tracking-wider bg-amber-400/20 text-amber-300 rounded-full border border-amber-400/40">
                    {t('privilege_badge')}
                  </span>
                  <span className="text-emerald-400 text-xs">{t('privilege_subbadge')}</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1 font-['Outfit']">
                  {t('privilege_title')}
                </h3>
                <p className="text-zinc-300 text-sm leading-relaxed">
                  {t('privilege_body')}
                </p>
                <p className="text-amber-200/90 text-xs mt-2 italic">
                  {t('privilege_eligibility')}
                </p>
              </div>
              <div className="md:self-stretch flex md:flex-col gap-2">
                <Button
                  className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold"
                  onClick={() => navigate('/auth')}
                  data-testid="privilege-cta-signup"
                >
                  {t('privilege_cta')}
                </Button>
                <Link
                  to="/cgv"
                  className="text-zinc-400 hover:text-white text-xs underline text-center self-center"
                  data-testid="privilege-cgv-link"
                >
                  {t('privilege_terms_link')}
                </Link>
              </div>
            </div>
          </div>

          {/* SEO text */}
          <p className="text-zinc-500 text-sm text-center mt-8 max-w-3xl mx-auto">
            <strong>VTC Nice aéroport</strong> - Transfert Nice Cannes - <strong>Navette aéroport Nice Monaco</strong> - 
            Chauffeur privé Saint-Tropez - VTC Côte d'Azur premium
          </p>
        </div>
      </section>

      {/* Why Choose Us - SEO Section */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
              {t('why_title').replace('YOUMI GREEN VTC Nice', '').replace('?', '').trim()} <span className="text-emerald-500">YOUMI GREEN VTC Nice</span> ?
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              {t('why_subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            <FeatureCard 
              icon={<Percent className="w-6 h-6" />}
              title={t('feature_competitive')}
              description={t('feature_competitive_desc')}
            />
            <FeatureCard 
              icon={<Car className="w-6 h-6" />}
              title={t('feature_recent_vehicle')}
              description={t('feature_recent_vehicle_desc')}
            />
            <FeatureCard 
              icon={<Droplets className="w-6 h-6" />}
              title={t('feature_water')}
              description={t('feature_water_desc')}
            />
            <FeatureCard 
              icon={<Shield className="w-6 h-6" />}
              title={t('feature_discretion')}
              description={t('feature_discretion_desc')}
            />
            <FeatureCard 
              icon={<Languages className="w-6 h-6" />}
              title={t('feature_multilingual')}
              description={t('feature_multilingual_desc')}
            />
            <FeatureCard 
              icon={<Battery className="w-6 h-6" />}
              title={t('feature_zero_emission')}
              description={t('feature_zero_emission_desc')}
            />
          </div>
        </div>
      </section>

      {/* Business Clients Section */}
      <section className="relative z-10 px-6 py-20 bg-[#121212]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 rounded-full text-blue-400 text-sm mb-4">
                <Building className="w-4 h-4" />
                {t('business_badge')}
              </div>
              <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
                <span className="text-emerald-500">{t('business_title').split(' - ')[0]}</span> - {t('business_title').split(' - ')[1]}
              </h2>
              <p className="text-zinc-400 mb-6">
                {t('business_desc')}
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: <Calendar className="w-5 h-5" />, text: t('business_congress') },
                  { icon: <Building className="w-5 h-5" />, text: t('business_hotels') },
                  { icon: <Briefcase className="w-5 h-5" />, text: t('business_trips') },
                  { icon: <FileText className="w-5 h-5" />, text: t('business_billing') },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-zinc-300">
                    <span className="text-blue-400">{item.icon}</span>
                    {item.text}
                  </div>
                ))}
              </div>

              <Button 
                className="btn-secondary flex items-center gap-2"
                onClick={() => setShowQuoteModal(true)}
                data-testid="business-quote-btn"
              >
                <FileText className="w-4 h-4" />
                {t('business_quote')}
              </Button>
            </div>
            
            <div className="glass-card rounded-2xl p-8 bg-gradient-to-br from-blue-500/10 to-emerald-500/10">
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-6">
                {t('business_advantages')}
              </h3>
              <ul className="space-y-4">
                {[
                  t('business_adv_1'),
                  t('business_adv_2'),
                  t('business_adv_3'),
                  t('business_adv_4'),
                  t('business_adv_5'),
                  t('business_adv_6')
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white font-['Outfit'] text-center mb-4">
            {t('pricing_title')}
          </h2>
          <p className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto">
            {t('pricing_subtitle')}
          </p>
          
          <div className="max-w-2xl mx-auto glass-card rounded-3xl p-8">
            <div className="text-center mb-6 p-4 bg-zinc-900/50 rounded-xl">
              <p className="text-zinc-400 text-sm">{t('pricing_pickup')}</p>
              <p className="text-3xl font-bold text-white font-['Outfit']">5€</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-6 bg-amber-500/10 rounded-2xl border border-amber-500/30">
                <Sun className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <p className="text-zinc-400 text-sm">{t('pricing_day')}</p>
                <p className="text-3xl font-bold text-white font-['Outfit']">1,70€</p>
                <p className="text-zinc-500 text-sm">{t('pricing_per_km')}</p>
              </div>
              <div className="text-center p-6 bg-indigo-500/10 rounded-2xl border border-indigo-500/30">
                <Moon className="w-8 h-8 text-indigo-400 mx-auto mb-3" />
                <p className="text-zinc-400 text-sm">{t('pricing_night')}</p>
                <p className="text-3xl font-bold text-white font-['Outfit']">1,95€</p>
                <p className="text-zinc-500 text-sm">{t('pricing_per_km')}</p>
              </div>
            </div>
            
            <div className="space-y-3 border-t border-zinc-800 pt-6">
              <div className="flex justify-between items-center">
                <span className="text-zinc-400 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {t('pricing_wait')}
                </span>
                <span className="text-lg font-bold text-white">0,35€/min</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">{t('pricing_min_fare')}</span>
                <span className="text-lg font-bold text-white">15€</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">{t('pricing_min_booking')}</span>
                <span className="text-lg font-bold text-emerald-500">{t('pricing_advance')}</span>
              </div>
            </div>
            
            <div className="flex gap-4 mt-8">
              <Button 
                className="btn-primary flex-1"
                onClick={handleBooking}
                data-testid="pricing-book-btn"
              >
                {t('pricing_book')}
              </Button>
              <Button 
                className="btn-secondary flex-1"
                onClick={() => setShowSimulator(true)}
              >
                {t('pricing_simulate')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area - SEO Section */}
      <section className="relative z-10 px-6 py-20 bg-[#121212]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full text-emerald-400 text-sm mb-4">
                <Map className="w-4 h-4" />
                {t('service_area_badge')}
              </div>
              <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
                <span className="text-emerald-500">{t('service_area_title').split(' ').slice(0, 2).join(' ')}</span> {t('service_area_title').split(' ').slice(2).join(' ')}
              </h2>
              <p className="text-zinc-400 mb-6">
                {t('service_area_desc')}
              </p>
              
              <div className="flex flex-wrap gap-2 mb-8">
                {SEO_CITIES.map((city) => (
                  <span 
                    key={city}
                    className="px-3 py-1 bg-zinc-800 rounded-full text-sm text-zinc-300 hover:bg-emerald-500/20 hover:text-emerald-400 transition-colors cursor-default"
                  >
                    VTC {city}
                  </span>
                ))}
              </div>

              <Button 
                className="btn-secondary flex items-center gap-2"
                onClick={() => setShowQuoteModal(true)}
                data-testid="quote-btn"
              >
                <MapPin className="w-4 h-4" />
                {t('service_area_quote')}
              </Button>
            </div>
            
            <div className="glass-card rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white font-['Outfit'] mb-6">
                {t('service_area_services')}
              </h3>
              <ul className="space-y-4">
                {[
                  t('service_1'),
                  t('service_2'),
                  t('service_3'),
                  t('service_4'),
                  t('service_5'),
                  t('service_6')
                ].map((service, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    {service}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="relative z-10 px-6 py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-full text-amber-400 text-sm mb-4">
              <Star className="w-4 h-4 fill-amber-400" />
              {t('reviews_title')}
            </div>
            <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
              {t('reviews_what_say')}
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              {t('reviews_satisfaction')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {(dynamicReviews.length > 0 ? dynamicReviews.slice(0, 6) : [
              { first_name: t('review_1_name'), rating: 5, comment: t('review_1_text'), created_at: "2026-02-15" },
              { first_name: t('review_2_name'), rating: 5, comment: t('review_2_text'), created_at: "2026-02-10" },
              { first_name: t('review_3_name'), rating: 5, comment: t('review_3_text'), created_at: "2026-01-20" }
            ]).map((review, i) => (
              <div key={i} className="glass-card rounded-2xl p-6 card-hover" data-testid={`review-card-${i}`}>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(review.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-zinc-300 mb-4 italic">"{review.comment}"</p>
                <div className="flex justify-between items-center">
                  <span className="text-white font-medium">{review.first_name || review.name}</span>
                  <span className="text-zinc-500 text-sm">
                    {review.created_at ? new Date(review.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Google Reviews Link */}
          <div className="text-center">
            <a 
              href="https://www.google.com/search?q=YOUMI+GREEN+VTC+Nice+avis"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-zinc-900 rounded-full font-medium hover:bg-zinc-100 transition-colors"
              data-testid="google-reviews-link"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('reviews_see_google')}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Accessibility Section - Book by Text */}
      <section className="relative z-10 px-6 py-12 bg-gradient-to-r from-blue-900/20 to-purple-900/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 rounded-full text-blue-400 text-sm mb-4">
            <MessageSquare className="w-4 h-4" />
            {t('accessibility_badge')}
          </div>
          <h3 className="text-2xl font-bold text-white font-['Outfit'] mb-4">
            {t('accessibility_title')}
          </h3>
          <p className="text-zinc-400 mb-6">
            {t('accessibility_desc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="btn-primary flex items-center gap-2"
              onClick={handleBooking}
              data-testid="accessibility-book-btn"
            >
              <Calendar className="w-4 h-4" />
              {t('accessibility_book')}
            </Button>
            <a 
              href="https://wa.me/33753202093?text=Bonjour%2C%20je%20souhaite%20r%C3%A9server%20une%20course.%20Je%20suis%20malentendant%2C%20merci%20de%20communiquer%20par%20%C3%A9crit."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-full font-medium hover:bg-green-700 transition-colors"
              data-testid="accessibility-whatsapp-btn"
            >
              <MessageSquare className="w-4 h-4" />
              {t('accessibility_whatsapp')}
            </a>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section id="reviews" className="relative z-10 px-6 py-16 bg-gradient-to-b from-zinc-900/50 to-[#09090b]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
              <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-4">
              {t('reviews_title')}
            </h2>
            <p className="text-zinc-400 text-lg">
              {t('reviews_subtitle')}
            </p>
          </div>

          {/* Sample Reviews */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-zinc-300 mb-4 italic">
                "{t('review_cta_text')}"
              </p>
              <p className="text-zinc-500 text-sm">— Marie L., Nice</p>
            </div>

            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                ))}
              </div>
              <p className="text-zinc-300 mb-4 italic">
                "{t('review_cta_text_2')}"
              </p>
              <p className="text-zinc-500 text-sm">— Thomas B., Cannes</p>
            </div>
          </div>

          {/* Google Reviews CTA */}
          <div className="text-center">
            <a 
              href="https://g.page/r/CXCi3jy1-jW0EAE/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black rounded-full font-bold text-lg hover:bg-zinc-200 transition-all shadow-lg hover:shadow-xl"
              data-testid="google-reviews-btn"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('reviews_leave')}
              <ExternalLink className="w-5 h-5" />
            </a>
            <p className="text-zinc-500 text-sm mt-4">
              {t('reviews_your_opinion')}
            </p>
          </div>
        </div>
      </section>

      {/* SEO Footer Text */}
      <section className="relative z-10 px-6 py-12 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-zinc-500 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('seo_text'), { ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'br'], ALLOWED_ATTR: [] }) }} />
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 bg-[#0a0a0a] border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-10 h-10" />
              <span className="font-bold font-['Outfit'] text-white">YOUMI GREEN VTC</span>
            </div>
            <div className="flex items-center gap-4 md:gap-6 flex-wrap justify-center text-sm">
              <Link to="/mentions-legales" className="text-zinc-400 hover:text-emerald-400 transition" data-testid="footer-mentions">{t('footer_legal')}</Link>
              <Link to="/cgv" className="text-zinc-400 hover:text-emerald-400 transition" data-testid="footer-cgv">{t('footer_cgv')}</Link>
              <Link to="/confidentialite" className="text-zinc-400 hover:text-emerald-400 transition" data-testid="footer-confidentialite">{t('footer_privacy')}</Link>
              <Link to="/suppression-donnees" className="text-zinc-400 hover:text-emerald-400 transition" data-testid="footer-suppression">{t('footer_deletion')}</Link>
              <Link to="/demande-devis" className="text-emerald-400 hover:text-emerald-300 transition font-medium" data-testid="footer-devis">{t('footer_quote')}</Link>
              <Link to="/admin/login" className="text-zinc-500 hover:text-emerald-400 transition" data-testid="footer-admin">{t('footer_admin')}</Link>
            </div>
          </div>
          <div className="text-zinc-500 text-sm text-center mt-6">
            <p>&copy; 2024 YOUMI GREEN VTC Nice - SASU - {t('footer_copyright')}</p>
            <p className="text-xs mt-1">{t('footer_seo_line')}</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Button */}
      <WhatsAppButton />

      {/* Price Simulator Modal */}
      <PriceSimulator isOpen={showSimulator} onClose={() => setShowSimulator(false)} />

      {/* Quote Request Modal */}
      <QuoteRequestModal isOpen={showQuoteModal} onClose={() => setShowQuoteModal(false)} />
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="glass-card rounded-2xl p-6 card-hover text-center">
    <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500 mb-4 mx-auto">
      {icon}
    </div>
    <h3 className="text-base font-bold text-white font-['Outfit'] mb-2">{title}</h3>
    <p className="text-zinc-400 text-sm">{description}</p>
  </div>
);
